import { formatDate } from '@angular/common';

/** Audit fields whose values are ISO timestamps and should render as dates. */
const AUDIT_DATE_FIELDS = new Set<string>(['validUntil', 'validFrom']);

/** Maps an audit field name to the form's existing ngx-translate label key. */
const AUDIT_FIELD_LABEL_KEYS: Record<string, string> = {
  displayName: 'servicePackages.editModal.displayName',
  validUntil: 'servicePackages.editModal.validUntil',
  comment: 'servicePackages.editModal.comment',
  serviceId: 'servicePackages.editModal.service',
  quantity: 'servicePackages.editModal.quantity',
  originalPrice: 'servicePackages.editModal.originalPrice',
  discountedPrice: 'servicePackages.editModal.discountedPrice',
  // The editor service emits fieldChanged: 'added' | 'removed' for whole-item
  // add/remove rows — these MUST match the backend strings, not 'addItem'.
  added: 'servicePackages.editModal.added',
  removed: 'servicePackages.editModal.removed',
};

/** Shown instead of a raw UUID when a serviceId can't be resolved to a name. */
const UNKNOWN_SERVICE_LABEL = 'სერვისი';

/** Minimal service lookup row used to resolve a serviceId to its display name. */
export interface AuditItemRef {
  serviceId: string;
  serviceName?: string;
}

/**
 * ngx-translate key for an audit row's changed field, reusing the form's
 * existing editModal labels. Falls back to the raw field name when unmapped.
 */
export function auditFieldLabelKey(field: string): string {
  return AUDIT_FIELD_LABEL_KEYS[field] ?? field;
}

/** A value that is present and not an empty string. */
function isSet(value: number | string | null | undefined): boolean {
  return value !== null && value !== undefined && value !== '';
}

/**
 * The amount to show beside an item in the edit history.
 *
 * beaut-593. This used to be `discountedPrice`, read straight off the snapshot,
 * and it displayed 0.00 ₾ on a removal that had refunded real money. Two
 * separate reasons, both of which had to be handled:
 *
 *  1. A package item sold at the original price arrives with
 *     `discountedPrice = 0` — "no discount entered", not "free". The backend has
 *     always resolved this with `packageItemEffectivePrice` (discounted when a
 *     real discount exists, otherwise original); the history panel was the one
 *     place that read the raw column and so reported 0 for every undiscounted
 *     line.
 *
 *  2. A removal is no longer valued at the catalogue price at all — it is worth
 *     the line's share of what the customer actually PAID. On a purchase sold
 *     for 450 against a 500 nominal, a 200-nominal line is worth 180, and no
 *     price column on the snapshot holds that number. So the backend records it
 *     as `valueRemoved`, and it is preferred whenever present.
 *
 * `valueRemoved` is what the line was WORTH, and is deliberately not called a
 * refund: whether money moved is a property of the whole EDIT, not of one line.
 * A removal can be netted against an addition in the same edit and leave the
 * customer owing money, and a NONE settlement moves nothing at all. The row
 * carries `settlementMethod` for a panel that wants to say which.
 *
 * `valueRemoved` is absent on rows written before that change and on `added`
 * rows (an addition carries the price charged), so the effective-price rule
 * below remains the fallback rather than dead code.
 */
function itemDisplayAmount(v: {
  valueRemoved?: number | string | null;
  originalPrice?: number | string | null;
  discountedPrice?: number | string | null;
}): number | string | null {
  if (isSet(v.valueRemoved)) return v.valueRemoved as number | string;
  // The frontend twin of the backend's `packageItemEffectivePrice`.
  if (isSet(v.discountedPrice) && Number(v.discountedPrice) > 0) {
    return v.discountedPrice as number | string;
  }
  return isSet(v.originalPrice) ? (v.originalPrice as number | string) : null;
}

/**
 * Renders an add/remove item snapshot object as
 * "<serviceName> × <quantity> · <amount> ₾".
 *
 * The backend stores these rows as a raw JSON snapshot
 * ({ serviceId, quantity, originalPrice, discountedPrice, valueRemoved? }); this
 * turns that into a human, Georgian-friendly line and resolves the serviceId
 * UUID to a name via `items`. Falls back to a generic service label (never the
 * UUID) when the service isn't in the supplied catalog.
 *
 * The amount is chosen by `itemDisplayAmount` — see there for why it is not
 * simply `discountedPrice`.
 */
export function formatItemValue(
  value: unknown,
  items: AuditItemRef[] = []
): string {
  if (!value || typeof value !== 'object') return String(value ?? '—');
  const v = value as {
    serviceId?: string;
    serviceName?: string | null;
    quantity?: number;
    valueRemoved?: number | string | null;
    originalPrice?: number | string | null;
    discountedPrice?: number | string | null;
  };
  // The snapshot's own name first. `items` holds the purchase's CURRENT lines,
  // so a REMOVED service is never in it and the lookup always fell through to
  // the generic label -- the one row where the name matters most was the one
  // row that could not show it. Rows written before the backend carried the
  // name still resolve through `items`, which is why that path stays.
  const name =
    v.serviceName ||
    items.find((i) => i.serviceId === v.serviceId)?.serviceName ||
    UNKNOWN_SERVICE_LABEL;
  let out = `${name} × ${v.quantity ?? 0}`;
  const amount = itemDisplayAmount(v);
  if (amount !== null) {
    out += ` · ${amount} ₾`;
  }
  return out;
}

/**
 * Renders an "added" audit item object as "<serviceName> × <quantity> · <price> ₾".
 * Retained for the package-purchase-drawer; delegates to formatItemValue.
 */
export function formatAddedValue(
  value: unknown,
  items: AuditItemRef[] = []
): string {
  return formatItemValue(value, items);
}

/**
 * Human-readable audit value. Date-typed fields render as dd/MM/yyyy;
 * empty/null values render as an em dash; item-snapshot objects (added /
 * removed rows) render via formatItemValue using the supplied service catalog
 * instead of raw JSON.
 */
export function formatAuditValue(
  field: string,
  value: unknown,
  items: AuditItemRef[] = [],
  locale = 'en-US'
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (AUDIT_DATE_FIELDS.has(field)) {
    try {
      return formatDate(value as string, 'dd/MM/yyyy', locale);
    } catch {
      // fall through to default rendering
    }
  }
  if (typeof value === 'object') return formatItemValue(value, items);
  return String(value);
}
